/**
 * UserInsights: the Identity pillar page (User Analytics), in Carbon.
 *
 *   1. PillarBrief  the action-first verdict (useIdentityBrief) beside the
 *                   pillar score tile (MFA member adoption, Identity swatch)
 *   2. PillarTabs   Overview / MFA / Licenses / Inactive / All users as line
 *                   tabs (URL-synced via ?tab=…; Overview elides from the URL)
 *                   with the inactivity-threshold menu in the actions slot
 *   3. Notices      info notifications above every tab body; the no-data
 *                   EmptyState replaces the body when the tenant has no scan
 *   4. Tab body     Overview: the Global Admin callout, the KPI strip, the MFA
 *                   tile (the two-part MFA bar, no per-member marks) and the
 *                   license tile in a condensed grid, the adoption trend and
 *                   the "People needing attention" DataTable. MFA / Licenses /
 *                   Inactive / All users wrap the UserIssuesTable DataTable
 *                   with the tab's pre-selected filter.
 *
 * Data wiring (NO new endpoints — every value is already on the wire):
 *   - getUserInsights              → summary, KPIs, license waste user count
 *   - getUserInsightsLicenses      → per-SKU two-part bars (assigned / unassigned)
 *   - getUserInsightsUsers         → "People needing attention" + tab tables
 *   - getUserInsightsStatus        → in-progress polling + last-refreshed
 *   - getPhishResistantMfaSummary  → the MFA bar and the method split
 *   - getUserInsightsTrends        → the adoption trend
 *   - getUserInsightsSettings      → inactivity threshold (tab-strip menu)
 *
 * Composer inputs follow the known-flags rule (blueprint §5.2 wiring note):
 * a count the page does not know is passed as undefined, never a fabricated 0.
 *
 * Voice contract (CLAUDE.md §Product Voice, as amended by blueprint §6):
 *   - Headlines lead with the action; ledes name the subject and its number
 *   - Upright type in sentence case, no kickers or dropcaps (DESIGN.md)
 *   - License waste is ALWAYS rendered as a USER COUNT on this page's KPIs
 *
 * §2.3 deferred items resolved here:
 *   - "Authenticator app" donut label → "Standard MFA"
 *   - $3,420/month recoverable → dropped from the Overview (no SKU pricing model there)
 *   - License utilization per-SKU dormant/disabled → tenant-level summary line only
 *   - "Rollout of FIDO2" annotation → dropped (no event store)
 *   - "Global Admin" role tag → jobTitle (real)
 *   - "142 sign-ins / 30d" → "Last sign-in Nd ago" relative-time
 *   - "Send setup link →" CTA → "Open user" deep-link (no write API)
 */

import { useState, useEffect, useMemo, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'

import { api } from '@/lib/api'
import { useTenantContext } from '@/hooks/useTenantContext'
import { useIdentityBrief } from '@/hooks/useIdentityBrief'
import { useTenantScanSchedule } from '@/hooks/useTenantScanSchedule'
import { isPaidSeatSku } from '@/lib/license-skus'
import { canEditScanSchedule } from '@/lib/scan-schedule'
import { lastAssessmentAt } from '@/lib/tenant-assessed'
import {
  calculateStarredBpaScore,
  useComplianceScores,
} from '@/hooks/useComplianceData'
import type { UserIssueFilter, UserDetailItem } from '@/types'
import { DEFAULT_INACTIVITY_THRESHOLD_DAYS } from '@/types'

import LicenseUtilization from '@/components/UserInsights/LicenseUtilization'
import UserInsightsTrends from '@/components/UserInsights/UserInsightsTrends'
import UserIssuesTable from '@/components/UserInsights/UserIssuesTable'
import GlobalAdminMfaCallout from '@/components/UserInsights/GlobalAdminMfaCallout'

import PillarBrief from '@/components/PostureBrief/PillarBrief'
import PillarTabs, {
  usePillarTab,
  type TabConfig,
} from '@/components/PostureBrief/PillarTabs'
import KpiStrip, { type KpiCell } from '@/components/PostureBrief/KpiStrip'
import EmptyState from '@/components/shared/EmptyState'

import { BigNumber } from '@/components/carbon/BigNumber'
import { Button } from '@/components/carbon/Button'
import { MenuButton } from '@/components/carbon/MenuButton'
import { CondensedGrid } from '@/components/carbon/CondensedGrid'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'
import {
  IconArrowRight,
  IconClose,
  IconExternal,
} from '@/components/carbon/icons'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { SegmentedMeter } from '@/components/carbon/SegmentedMeter'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { Skeleton } from '@/components/carbon/Skeleton'
import { Spinner } from '@/components/carbon/Spinner'
import { Tile } from '@/components/carbon/Tile'
import { TileHeader } from '@/components/carbon/TileHeader'
import { WorkloadBars } from '@/components/carbon/WorkloadBars'

const THRESHOLD_OPTIONS = [30, 60, 90, 120, 180, 365] as const

/**
 * Blueprint-specified copy (easy-tcm R4/R5) shown while Exchange mailbox
 * classification has not yet run for the tenant. Transient provisioning state,
 * not an error — advisor voice frames it as "still finishing".
 */
const CLASSIFICATION_REFINING_COPY =
  'user metrics are refining as Exchange finishes setting up.'

/** Tab IDs for the User Analytics page. URL-synced via PillarTabs. */
type UserAnalyticsTab = 'overview' | 'mfa' | 'licenses' | 'inactive' | 'all'
const TAB_IDS: ReadonlyArray<UserAnalyticsTab> = [
  'overview',
  'mfa',
  'licenses',
  'inactive',
  'all',
]

/** Rows per page in the user-issues tables (the server query's limit). */
const USERS_PAGE_SIZE = 20

/** Each tab pre-selects the filter that drives the embedded UserIssuesTable.
 *  Overview is "All" because it doesn't render the table — the filter only
 *  matters when the user navigates over to a tab that does. */
const TAB_FILTER: Record<UserAnalyticsTab, UserIssueFilter> = {
  overview: 'All',
  mfa: 'NoMfa',
  licenses: 'LicenseWaste',
  inactive: 'InactiveMembers',
  all: 'All',
}

export default function UserInsights() {
  const {
    defaultTenantId,
    isLoading: contextLoading,
    userTenants,
    isGlobalAdmin,
  } = useTenantContext()
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(
    undefined,
  )

  // The active tab. Driven by the PillarTabs hook so the URL stays in sync.
  const [activeTab] = usePillarTab(TAB_IDS, 'overview')

  // Scope param — narrows the user-issues table to a named cohort. Today
  // only `globalAdmins` is honored (other values are reserved for future
  // cohorts). The Operations panel emits identity alerts that link here with
  // `?scope=globalAdmins` to honor "1 GA without MFA" → land on the GA, not
  // the entire no-MFA list. Composes ON TOP of the active tab's base filter.
  const [searchParams, setSearchParams] = useSearchParams()
  const rawScope = searchParams.get('scope')
  const scopeGlobalAdmins = rawScope === 'globalAdmins'

  /** Remove only the `scope` param while keeping `tab` and any others.
   *  Used by the "Clear" affordance on the GA filter notification. */
  const clearScope = () => {
    setSearchParams(
      prev => {
        prev.delete('scope')
        return prev
      },
      { replace: true },
    )
  }

  // The filter state for the user-issues table. We keep it as page-level state
  // so the user can switch filters inside the All-Users tab without re-reading
  // the URL — but each tab change auto-resets it via the effect below.
  const [selectedFilter, setSelectedFilter] = useState<UserIssueFilter>('All')
  const [currentPage, setCurrentPage] = useState(1)
  const [trendDays, setTrendDays] = useState(90)
  const queryClient = useQueryClient()

  // When the user switches tabs, snap the underlying user-table filter to the
  // tab-bound default. Inside All-Users the user is then free to change the
  // filter; the next tab change resets it again.
  useEffect(() => {
    setSelectedFilter(TAB_FILTER[activeTab])
    setCurrentPage(1)
  }, [activeTab])

  // Set default tenant when context loads. Same logic the legacy page used so
  // empty / fallback states behave identically.
  useEffect(() => {
    if (!contextLoading) {
      if (isGlobalAdmin) {
        if (defaultTenantId && defaultTenantId !== undefined) {
          setSelectedTenantId(defaultTenantId)
        }
      } else {
        if (defaultTenantId !== undefined) {
          setSelectedTenantId(defaultTenantId)
        } else if (userTenants.length > 0) {
          setSelectedTenantId(userTenants[0].tenantId)
        }
      }
    }
  }, [contextLoading, defaultTenantId, isGlobalAdmin, userTenants])

  // ---- Data queries (same query keys + cadence as legacy page so the cache
  //      stays warm when navigating between Posture pages) ----

  const {
    data: insights,
    isLoading: insightsLoading,
    error: insightsError,
    refetch: refetchInsights,
  } = useQuery({
    queryKey: ['user-insights', selectedTenantId],
    queryFn: () =>
      selectedTenantId ? api.getUserInsights(selectedTenantId) : Promise.resolve(null),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: insightsSettings } = useQuery({
    queryKey: ['user-insights-settings', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsSettings(selectedTenantId)
        : Promise.resolve(null),
    enabled: !!selectedTenantId,
  })

  const { data: licenses, isLoading: licensesLoading } = useQuery({
    queryKey: ['user-insights-licenses', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsLicenses(selectedTenantId)
        : Promise.resolve([]),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: usersData, isLoading: usersLoading } = useQuery({
    // `scopeGlobalAdmins` is part of the cache key so React Query holds
    // independent caches for scoped vs unscoped views. Without this, the
    // cached unscoped page would be served when the scope flips on, briefly
    // misrepresenting the cohort before refetch.
    queryKey: [
      'user-insights-users',
      selectedTenantId,
      selectedFilter,
      currentPage,
      scopeGlobalAdmins,
    ],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsUsers(
            selectedTenantId,
            selectedFilter,
            currentPage,
            USERS_PAGE_SIZE,
            scopeGlobalAdmins,
          )
        : Promise.resolve({
            items: [],
            total: 0,
            page: 1,
            limit: USERS_PAGE_SIZE,
            totalPages: 0,
          }),
    enabled: !!selectedTenantId,
  })

  const { data: refreshStatus, isLoading: statusLoading } = useQuery({
    queryKey: ['user-insights-status', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsStatus(selectedTenantId)
        : Promise.resolve(null),
    enabled: !!selectedTenantId,
    // Faster cadence so the in-progress status clears quickly after a scan ends.
    refetchInterval: 10000,
  })

  const { data: phishResistantMfa, isLoading: phishLoading } = useQuery({
    queryKey: ['userInsights', 'phishResistantMfa', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getPhishResistantMfaSummary(selectedTenantId)
        : Promise.resolve(null),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: ['userInsights', 'trends', selectedTenantId, trendDays],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsTrends(selectedTenantId, trendDays)
        : Promise.resolve(null),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  // Top-6 users for "People needing attention". Limit=6 keeps the list short;
  // page=1 is fine because we sort server-side by issueType priority.
  const { data: noMfaUsers, isLoading: noMfaUsersLoading } = useQuery({
    queryKey: ['userInsights', 'people-needing-attention', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsUsers(selectedTenantId, 'NeedsAttention', 1, 6)
        : Promise.resolve({
            items: [],
            total: 0,
            page: 1,
            limit: 6,
            totalPages: 0,
          }),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  // Named members in the No-MFA cohort specifically — drives the MFA tile's
  // named lead. Kept separate from the mixed NeedsAttention list above so the
  // names always come from (and stay consistent with) the MFA bar's no-MFA
  // population, not whatever issue happens to top the priority list.
  const { data: noMfaCohort } = useQuery({
    queryKey: ['userInsights', 'no-mfa-cohort', selectedTenantId],
    queryFn: () =>
      selectedTenantId
        ? api.getUserInsightsUsers(selectedTenantId, 'NoMfa', 1, 3)
        : Promise.resolve({
            items: [],
            total: 0,
            page: 1,
            limit: 3,
            totalPages: 0,
          }),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  // The four Guardian-Score components, passed to PillarBrief's `components`
  // prop. The Carbon pillar tile no longer draws a breakdown (D22), but the
  // queries stay so the Command Center's cache stays warm across pages.
  const { data: secureScoreSummary } = useQuery({
    queryKey: ['secure-score-summary', selectedTenantId],
    queryFn: () => api.getSecureScoreSummary(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: dashboardStats } = useQuery({
    queryKey: ['dashboard', selectedTenantId],
    queryFn: () => api.getDashboardStats(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: complianceScores } = useComplianceScores(selectedTenantId)

  // Mirrors Dashboard.tsx — uses `weightedScore` so the composite component
  // matches the BPA pillar's hero metric (which sources `weightedScore` via
  // `transformToDisplayFrameworks`).
  const bpaStarredScore = useMemo(() => {
    if (!complianceScores?.standardScores) return undefined
    const mapped = complianceScores.standardScores.map(s => ({
      code: s.standardCode,
      score: s.weightedScore,
      weight: s.weight,
      includeInGuardianScore: s.includeInGuardianScore,
    }))
    return calculateStarredBpaScore(mapped) ?? undefined
  }, [complianceScores?.standardScores])

  // ---- Mutations ----

  const updateThresholdMutation = useMutation({
    mutationFn: (newThreshold: number) =>
      api.updateUserInsightsSettings(selectedTenantId!, {
        inactivityThresholdDays: newThreshold,
      }),
    onSuccess: (_data, newThreshold) => {
      queryClient.invalidateQueries({
        queryKey: ['user-insights-settings', selectedTenantId],
      })
      queryClient.invalidateQueries({ queryKey: ['user-insights', selectedTenantId] })
      queryClient.invalidateQueries({
        queryKey: ['user-insights-users', selectedTenantId],
      })
      toast.success(
        `Inactivity threshold updated to ${newThreshold} days. A new scan is needed to recalculate user data.`,
      )
    },
    onError: (err: any) => {
      const errorMessage =
        err?.response?.data?.message || err?.message || 'Failed to update settings'
      toast.error(errorMessage)
    },
  })

  // ---- Derived view-model ----

  const currentThreshold =
    insightsSettings?.inactivityThresholdDays ??
    insights?.inactivityThresholdDays ??
    DEFAULT_INACTIVITY_THRESHOLD_DAYS

  const isNonDefaultThreshold = currentThreshold !== DEFAULT_INACTIVITY_THRESHOLD_DAYS

  // GA-scope narrowing. When the URL carries `?scope=globalAdmins` (emitted
  // by the Operations panel's identity alerts: GA-no-MFA + dormant-GA), the
  // user-issues query passes `globalAdminsOnly=true` to the server so the
  // result set is narrowed AND paginated correctly under the active filter.
  // Composes ON TOP of the tab's base filter (e.g. tab=mfa + scope honored
  // → only GAs without MFA, paged authoritatively).

  // Filter counts for the user-issues table's tabs and filter select. Same map
  // the legacy page computed; only the rendering host changes.
  const filterCounts = useMemo(() => {
    if (!insights) return undefined
    const mfaEnabled = insights.mfaEnabledMembers ?? 0
    return {
      All: insights.totalUsers ?? 0,
      ActiveMembers: insights.activeMemberUsers ?? 0,
      ActiveGuests: insights.activeGuestUsers ?? 0,
      NeedsAttention: insights.needsAttentionUsers ?? 0,
      NoMfa: insights.noMfaEnabledUsers ?? 0,
      NoPhishResistantMfa: mfaEnabled - (insights.phishResistantMfaUsers ?? 0),
      HasPhishResistantMfa: insights.phishResistantMfaUsers ?? 0,
      LicenseWaste: insights.totalLicenseWaste ?? 0,
      DisabledWithLicenses: insights.disabledUsersWithLicenses ?? 0,
      InactiveWithLicenses: insights.inactiveUsersWithLicenses ?? 0,
      InactiveMembers: insights.inactiveMemberUsers ?? 0,
      InactiveGuests: insights.inactiveGuestUsers ?? 0,
    } as Record<string, number>
  }, [insights])

  // Exchange mailbox classification hasn't run for this scan → the shared/
  // resource-mailbox exclusions behind the No-MFA / Needs-attention / phish
  // counts are unreliable, so those surfaces show a transient refining state
  // instead of the inflated numbers (easy-tcm R4/R5). Strict `=== false` keeps
  // older summaries (undefined) ungated; only gate when there is data to gate.
  const classificationRefining =
    insights?.hasData === true && insights.mailboxClassificationAvailable === false

  // The tenant has no completed user scan: every count on the summary is a
  // placeholder zero, so nothing below may present it as a real figure.
  const noScanData = insights?.hasData === false
  const showNoData = !statusLoading && !!insights && !insights.hasData

  // The score tile's figure is the live MFA-member-adoption percent. This DTO
  // field is classification-dependent: it comes from a member query that
  // excludes shared/resource mailboxes, so a classification failure counts
  // those MFA-less mailboxes as members and DEFLATES adoption. Gate the
  // displayed score to null → "—" while refining, and while there is no scan.
  const identityScore = insights?.mfaMemberAdoptionPercentage ?? null
  const displayIdentityScore = classificationRefining || noScanData ? null : identityScore

  // Top action for the brief composer. Mirrors PillarIdentity's logic:
  // crit if anyone is missing MFA, then dormant cleanup, then license waste.
  const topAction = useMemo(() => {
    if (!insights) return undefined
    // When classification is refining the No-MFA count is untrusted, so treat it
    // as 0 here — the top action falls through to dormant / license-waste, both
    // of which are unaffected by the mailbox exclusion.
    const refining =
      insights.hasData === true && insights.mailboxClassificationAvailable === false
    const noMfa = refining ? 0 : (insights.noMfaEnabledUsers ?? 0)
    const dormant = insights.inactiveMemberUsers ?? 0
    const waste = insights.totalLicenseWaste ?? 0
    if (noMfa > 0) {
      return {
        pillar: 'identity' as const,
        title: `Enroll ${noMfa} ${noMfa === 1 ? 'member' : 'members'} on MFA`,
        impact: 'crit' as const,
      }
    }
    if (dormant > 0) {
      return {
        pillar: 'identity' as const,
        title: `Review ${dormant} dormant ${dormant === 1 ? 'account' : 'accounts'}`,
        impact: 'high' as const,
      }
    }
    if (waste > 0) {
      return {
        pillar: 'identity' as const,
        title: `Reclaim ${waste} ${waste === 1 ? 'license' : 'licenses'}`,
        impact: 'med' as const,
      }
    }
    return undefined
  }, [insights])

  // Period adoption delta from trends, when we have it. The history endpoint
  // surfaces dated points, so we diff first vs last to get the period delta.
  const scoreDeltas = (() => {
    const history = trendsData?.history
    if (!history || history.length < 2) return undefined
    const first = history[0]?.mfaMemberAdoptionPercentage
    const last = history[history.length - 1]?.mfaMemberAdoptionPercentage
    if (typeof first !== 'number' || typeof last !== 'number') return undefined
    return { score: last - first, days: trendDays }
  })()

  // No completed user scan on a tenant Guardian has never assessed: the brief
  // takes the Command Center's first-assessment line (finding 5), by role
  // (same cached /me read as useTenantContext) and by the tenant's schedule
  // (the shared hook). On an assessed tenant it says only that no user data
  // has arrived yet.
  const { data: currentUser, isLoading: currentUserLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: api.getCurrentUser,
    staleTime: 5 * 60 * 1000,
  })
  const firstAssessmentPending =
    noScanData && lastAssessmentAt(userTenants.find(t => t.tenantId === selectedTenantId)) === null
  const canRunScans = currentUser?.isPlatformAdmin === true
  const scanSchedule = useTenantScanSchedule({
    tenantId: selectedTenantId,
    neverScanned: firstAssessmentPending,
    currentUser,
    currentUserLoading,
  })

  // Known-flags rule (blueprint §5.2 wiring note): a count the page does not
  // know is undefined, never 0. `noMfaGlobalAdmins` passes straight through
  // (undefined until the summary loads); the No-MFA count is untrusted while
  // classification is refining and absent until there is a scan.
  const brief = useIdentityBrief({
    tenantId: selectedTenantId,
    // Gated score so the composed headline/lede never narrates the deflated
    // adoption figure while classification is refining.
    score: displayIdentityScore,
    deltas: scoreDeltas,
    topAction,
    criticalCount:
      classificationRefining || noScanData ? undefined : insights?.noMfaEnabledUsers,
    noMfaGlobalAdmins: insights?.noMfaGlobalAdmins,
    // The brief's one Soft Green button: the cohort list of members without MFA.
    primaryHref: '/user-insights?tab=mfa',
    // The provenance line: `getUserInsightsStatus().lastRefreshedAt` (polled
    // every 10s above). Undefined while the status query is in flight, so the
    // line is not drawn instead of briefly reading "Awaiting first assessment".
    assessedAt: refreshStatus ? refreshStatus.lastRefreshedAt ?? null : undefined,
    // No completed user scan: the no-data brief, never the loading one.
    noData: noScanData,
    firstAssessment: firstAssessmentPending
      ? {
          scanHref: canRunScans ? '/admin/scans' : undefined,
          settingsHref: !canRunScans && canEditScanSchedule(currentUser) ? '/tenants' : undefined,
          schedule: scanSchedule,
        }
      : undefined,
  })

  // Guardian Score components. Accepted by PillarBrief for compatibility; the
  // Carbon pillar tile draws no breakdown. The 'MFA adoption' row is a second
  // rendering of the classification-dependent adoption %, so it drops while
  // refining (F2); the other three components are unaffected.
  const compositeBars = useMemo(
    () => [
      {
        label: 'Baseline drift',
        weight: '20%',
        value: Math.round(dashboardStats?.complianceScore?.percentage ?? 0),
        fillClass: 'bg-cat-baseline',
      },
      {
        label: 'Best practice',
        weight: '30%',
        value: Math.round(bpaStarredScore ?? 0),
        fillClass: 'bg-cat-bpa',
      },
      ...(classificationRefining
        ? []
        : [
            {
              label: 'MFA adoption',
              weight: '20%',
              value: Math.round(insights?.mfaMemberAdoptionPercentage ?? 0),
              fillClass: 'bg-cat-identity',
            },
          ]),
      {
        label: 'M365 Secure Score',
        weight: '30%',
        value: Math.round(secureScoreSummary?.score?.percentage ?? 0),
        fillClass: 'bg-cat-secure',
      },
    ],
    [
      classificationRefining,
      dashboardStats?.complianceScore?.percentage,
      bpaStarredScore,
      insights?.mfaMemberAdoptionPercentage,
      secureScoreSummary?.score?.percentage,
    ],
  )

  // Tab count tags only for counts from a real scan (never a placeholder 0).
  const hasScanData = insights?.hasData === true
  const tabs: TabConfig<UserAnalyticsTab>[] = [
    { id: 'overview', label: 'Overview' },
    {
      id: 'mfa',
      label: 'MFA',
      // Suppress the count while it's untrusted — the tab still routes to the
      // cohort list, which stays visible under the refining advisory.
      badge: hasScanData && !classificationRefining ? (insights?.noMfaEnabledUsers ?? null) : null,
    },
    {
      id: 'licenses',
      label: 'Licenses',
      // §2.3 explicit: totalLicenseWaste is a USER count, not a SKU count.
      // The badge mirrors that.
      badge: hasScanData ? (insights?.totalLicenseWaste ?? null) : null,
    },
    {
      id: 'inactive',
      label: 'Inactive',
      badge: hasScanData ? (insights?.inactiveMemberUsers ?? null) : null,
    },
    { id: 'all', label: 'All users' },
  ]

  // ---- Early returns (loading / empty / error) ----

  if (contextLoading) {
    return (
      <p className="m-0 flex items-center gap-2 py-12 text-body text-text-secondary">
        <Spinner />
        Loading tenant information…
      </p>
    )
  }

  if (!selectedTenantId && isGlobalAdmin && userTenants.length > 0) {
    return (
      <Tile as="section" aria-label="User analytics" padding={24} className="mt-8">
        <EmptyState
          headline="Select a tenant to see its user analytics."
          body="Use the tenant switcher in the side nav to choose one."
        />
      </Tile>
    )
  }

  if (!selectedTenantId && userTenants.length === 0) {
    return (
      <Tile as="section" aria-label="User analytics" padding={24} className="mt-8">
        <EmptyState
          headline="No tenants are assigned to you yet."
          body="Ask your administrator to grant you access to a tenant."
        />
      </Tile>
    )
  }

  if (insightsError) {
    return (
      <InlineNotification
        kind="error"
        title="User analytics did not load."
        className="mt-8"
        actions={
          <Button kind="tertiary" size="sm" onClick={() => refetchInsights()}>
            Retry
          </Button>
        }
      >
        Guardian could not read this tenant&rsquo;s user summary.
      </InlineNotification>
    )
  }

  // ---- Main render ----

  const usersTable = (
    <UserIssuesTable
      users={usersData?.items ?? []}
      total={usersData?.total ?? 0}
      currentPage={currentPage}
      totalPages={usersData?.totalPages || 0}
      selectedFilter={selectedFilter}
      onFilterChange={(filter) => {
        setSelectedFilter(filter)
        setCurrentPage(1)
      }}
      onPageChange={setCurrentPage}
      isLoading={usersLoading}
      inactivityThresholdDays={currentThreshold}
      tenantId={selectedTenantId}
      filterCounts={filterCounts}
      resourceAccountCount={insights?.resourceAccountCount}
      globalAdminsOnly={scopeGlobalAdmins}
      pageSize={USERS_PAGE_SIZE}
    />
  )

  const scopeBanner = (
    <GlobalAdminScopeBanner
      active={scopeGlobalAdmins}
      onClear={clearScope}
      visibleCount={usersData?.items.length ?? 0}
      total={usersData?.total ?? 0}
    />
  )

  return (
    <div className="min-w-0">
      <PillarBrief
        brief={brief}
        score={displayIdentityScore}
        components={compositeBars}
        tone="identity"
        isLoading={insightsLoading}
        scoreLabel="MFA adoption"
        // No period delta without two trend points: never draw a fabricated
        // "No change" tag.
        hideDelta={!scoreDeltas || displayIdentityScore === null}
        // The provenance line comes from the composer (`assessedAt` above), so
        // no `lastRefreshedAt` segment: one time per line.
      />

      <PillarTabs
        tabs={tabs}
        defaultTab="overview"
        tone="identity"
        ariaLabel="User Analytics view"
        actions={
          <TabActions
            currentThreshold={currentThreshold}
            onUpdateThreshold={(days) => updateThresholdMutation.mutate(days)}
            isUpdatingThreshold={updateThresholdMutation.isPending}
            tenantSelected={!!selectedTenantId}
          />
        }
      />

      {/* Scan-in-progress / non-default threshold / no-data notices live ABOVE
          the per-tab body so they are visible regardless of the active tab. */}
      <NoticeBlock
        refreshStatus={refreshStatus}
        statusLoading={statusLoading}
        insights={insights}
        isNonDefaultThreshold={isNonDefaultThreshold}
        currentThreshold={currentThreshold}
        classificationRefining={classificationRefining}
      />

      {!showNoData && (
        <div
          role="tabpanel"
          id={`panel-${activeTab}`}
          aria-labelledby={`tab-${activeTab}`}
          className="min-w-0"
        >
          {activeTab === 'overview' && (
            <>
              {/* GA-MFA operational-risk callout. Renders above the Overview
                  body when `noMfaGlobalAdmins > 0`, returns null otherwise.
                  NOT gated by classificationRefining: a Global Admin is never a
                  shared/room/equipment mailbox, so the mailbox-classification
                  exclusion doesn't touch the GA counts. Hiding this would
                  suppress a true critical and contradict the Command Center
                  Operations rail, which shows GA-no-MFA ungated. */}
              <GlobalAdminMfaCallout count={insights?.noMfaGlobalAdmins ?? 0} />
              <OverviewTab
                insights={insights}
                phishResistantMfa={phishResistantMfa}
                phishLoading={phishLoading}
                licenses={licenses ?? []}
                licensesLoading={licensesLoading}
                trendsData={trendsData ?? null}
                trendsLoading={trendsLoading}
                trendDays={trendDays}
                onTrendDaysChange={setTrendDays}
                peopleNeedingAttention={noMfaUsers?.items ?? []}
                peopleNeedingAttentionLoading={noMfaUsersLoading}
                noMfaNames={noMfaCohort?.items ?? []}
                currentThreshold={currentThreshold}
                classificationRefining={classificationRefining}
              />
            </>
          )}

          {activeTab === 'mfa' && (
            <>
              {scopeBanner}
              {usersTable}
            </>
          )}

          {activeTab === 'licenses' && (
            <div className="space-y-12">
              <LicenseUtilization
                licenses={licenses ?? []}
                insights={insights}
                isLoading={licensesLoading}
              />
              <div>
                {scopeBanner}
                {usersTable}
              </div>
            </div>
          )}

          {activeTab === 'inactive' && (
            <>
              {scopeBanner}
              {usersTable}
            </>
          )}

          {activeTab === 'all' && (
            <>
              {scopeBanner}
              {usersTable}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Tab actions slot — inactivity threshold menu                               */
/* -------------------------------------------------------------------------- */

/** Inactivity-threshold menu. Lives in the PillarTabs `actions` slot so the
 *  control stays docked to the tab strip. A MenuButton choosing one value: the
 *  current threshold carries the check. */
export function TabActions(props: {
  currentThreshold: number
  onUpdateThreshold: (days: number) => void
  isUpdatingThreshold: boolean
  tenantSelected: boolean
}) {
  if (!props.tenantSelected) return null

  return (
    <MenuButton
      label={`Inactive after ${props.currentThreshold} days`}
      align="end"
      disabled={props.isUpdatingThreshold}
      items={THRESHOLD_OPTIONS.map((days) => ({
        id: String(days),
        label:
          days === DEFAULT_INACTIVITY_THRESHOLD_DAYS ? `${days} days (default)` : `${days} days`,
        selected: days === props.currentThreshold,
        onClick: () => props.onUpdateThreshold(days),
      }))}
    />
  )
}

/* -------------------------------------------------------------------------- */
/* Notice block                                                               */
/* -------------------------------------------------------------------------- */

/** Inline notices that need to render regardless of active tab. Kept in their
 *  own component so the main render reads clean. Exported as a test seam for the
 *  classification-refining advisory (the page container itself is not SSR-
 *  renderable — its tenant selection is useEffect-driven). */
export function NoticeBlock(props: {
  refreshStatus: { status?: string; lastRefreshedAt?: string; errorMessage?: string } | null | undefined
  statusLoading: boolean
  insights: { hasData?: boolean } | null | undefined
  isNonDefaultThreshold: boolean
  currentThreshold: number
  classificationRefining: boolean
}) {
  const items: ReactNode[] = []

  // Exchange mailbox classification still provisioning — one advisory note
  // above every tab body, so it covers both the Overview tiles and the
  // No-MFA / Needs-attention tables. A neutral info notification: transient
  // framing, never error styling.
  if (props.classificationRefining) {
    items.push(
      <InlineNotification key="refining" kind="info" title="Still finishing setup.">
        Some {CLASSIFICATION_REFINING_COPY} License figures are accurate now, and the people
        listed stay useful as leads.
      </InlineNotification>,
    )
  }

  // A failed scheduled refresh leaves the last good data in place, so it is
  // not a blocking error: neutral info, with the server's message when it
  // sent one.
  if (props.refreshStatus?.status === 'Failed') {
    items.push(
      <InlineNotification key="failed" kind="info" title="The last user analytics refresh failed.">
        {props.refreshStatus?.errorMessage
          ? props.refreshStatus.errorMessage
          : 'Guardian could not complete the most recent refresh. The data below may be stale until the next one finishes.'}
      </InlineNotification>,
    )
  }

  if (props.refreshStatus?.status === 'InProgress') {
    items.push(
      <p key="in-progress" role="status" className="m-0 flex items-center gap-2 text-body text-text-secondary">
        <Spinner />
        User analytics scan in progress…
      </p>,
    )
  }

  if (props.isNonDefaultThreshold) {
    items.push(
      <InlineNotification key="threshold" kind="info" title="Custom inactivity threshold.">
        Inactivity data is calculated using a{' '}
        <b className="font-semibold text-text-primary">{props.currentThreshold}-day</b> threshold
        instead of the default {DEFAULT_INACTIVITY_THRESHOLD_DAYS} days. Users are flagged as
        inactive if they have had no sign-in activity for {props.currentThreshold}+ days.
      </InlineNotification>,
    )
  }

  // No completed scan: the tab bodies are hidden and this empty state stands
  // in for them (the page applies the same condition).
  if (!props.statusLoading && props.insights && !props.insights.hasData) {
    items.push(
      <Tile key="no-data" as="section" aria-label="User analytics" padding={24}>
        <EmptyState
          headline="Guardian has not scanned this tenant's users yet."
          body="User, license and MFA information appears here after the first scan."
        />
      </Tile>,
    )
  }

  if (items.length === 0) return null
  return <div className="mb-8 flex flex-col gap-4">{items}</div>
}

/* -------------------------------------------------------------------------- */
/* Global Admin scope notification                                            */
/* -------------------------------------------------------------------------- */

/** The notification above the user-issues table when the page is scoped to
 *  Global Admins via `?scope=globalAdmins`. The Operations panel emits
 *  identity alerts ("Two Global Admins without MFA") that link to
 *  `/user-insights?tab=mfa&scope=globalAdmins` — this is the visible contract
 *  that the page actually honored the scope, not just the tab. An info
 *  notification (role="status", so it is announced politely).
 *
 *  Voice: short, named subjects, no em-dash. Clear removes only the `scope`
 *  param so the user keeps their current tab.
 *
 *  The server applies the GA narrowing via `globalAdminsOnly=true`, so
 *  `total` is authoritative for the GA-scoped cohort under the active filter. */
function GlobalAdminScopeBanner({
  active,
  onClear,
  visibleCount,
  total,
}: {
  active: boolean
  onClear: () => void
  /** Number of GA rows on the current page. */
  visibleCount: number
  /** Authoritative server-side count of GA rows under the active filter. */
  total: number
}) {
  if (!active) return null

  // Period-then-clause framing per CLAUDE.md voice. No em-dash. ≤ 14 words.
  // When server total === visible count we have the whole cohort on one
  // page, so drop the "M total" trailing clause and keep it tight.
  const lead = 'Filtered to Global Admins.'
  const tail =
    total > visibleCount
      ? `${visibleCount} on this page · ${total} total.`
      : visibleCount === 1
        ? 'One on this page.'
        : `${visibleCount} on this page.`

  return (
    <InlineNotification
      kind="info"
      title={lead}
      className="mb-4"
      actions={
        <Button kind="ghost" size="sm" onClick={onClear} icon={<IconClose />}>
          Clear
        </Button>
      }
    >
      {tail}
    </InlineNotification>
  )
}

/* -------------------------------------------------------------------------- */
/* Overview tab                                                               */
/* -------------------------------------------------------------------------- */

/** Compose the Overview tab body. Pure presentation given upstream data. */
function OverviewTab(props: {
  insights: import('@/types').UserInsightsSummary | null | undefined
  phishResistantMfa: import('@/types').PhishResistantMfaSummary | null | undefined
  /** The phish-resistant summary is still loading: the MFA tile shows a skeleton. */
  phishLoading?: boolean
  licenses: import('@/types').LicenseSkuSummary[]
  licensesLoading: boolean
  trendsData: import('@/types').UserInsightsTrendsResult | null
  trendsLoading: boolean
  trendDays: number
  onTrendDaysChange: (days: number) => void
  peopleNeedingAttention: UserDetailItem[]
  peopleNeedingAttentionLoading: boolean
  /** Members specifically in the No-MFA cohort — names for the MFA tile lead. */
  noMfaNames: UserDetailItem[]
  currentThreshold: number
  /** EXO mailbox classification hasn't run this scan → gate the No-MFA tile and
   *  the MFA bar (which exclude shared/resource mailboxes). */
  classificationRefining: boolean
}) {
  const insights = props.insights
  const phish = props.phishResistantMfa
  const refining = props.classificationRefining

  const noMfa = insights?.noMfaEnabledUsers ?? 0
  const licenseWasteUsers = insights?.totalLicenseWaste ?? 0
  const dormant = insights?.inactiveMemberUsers ?? 0
  const disabledLicensed = insights?.disabledUsersWithLicenses ?? 0
  const inactiveLicensed = insights?.inactiveUsersWithLicenses ?? 0
  // While the summary is still loading, every figure is a dash, not a zero.
  const known = !!insights

  // KPI tiles. Per §5 the License-waste tile is a USER count and its line is
  // the disabled + dormant breakdown — explicitly NEVER $/mo.
  const kpiCells: KpiCell[] = [
    {
      id: 'no-mfa',
      // While classification is refining the No-MFA count excludes shared/
      // resource mailboxes that were never classified — show a placeholder
      // rather than the inflated figure.
      numeral: refining || !known ? '—' : noMfa.toLocaleString(),
      label: 'Members without MFA',
      stat: refining
        ? 'Refining as Exchange finishes setting up.'
        : noMfa === 0
          ? 'Every enabled member has MFA.'
          : noMfa === 1
            ? 'One enabled member still signs in without MFA.'
            : 'Enabled members who still sign in without MFA.',
      severity: refining ? 'tone' : noMfa > 0 ? 'crit' : 'tone',
    },
    {
      id: 'license-waste',
      numeral: known ? licenseWasteUsers.toLocaleString() : '—',
      // §2.3: NOT "SKUs", NOT "$N/mo". User count.
      label: 'License waste',
      stat: `${disabledLicensed.toLocaleString()} disabled and ${inactiveLicensed.toLocaleString()} dormant accounts still hold a license.`,
      severity: licenseWasteUsers > 0 ? 'warn' : 'tone',
    },
    {
      id: 'dormant',
      numeral: known ? dormant.toLocaleString() : '—',
      label: 'Dormant members',
      stat: `No sign-in activity in ${props.currentThreshold}+ days.`,
      severity: 'tone',
    },
  ]

  return (
    <div className="min-w-0">
      <KpiStrip tone="identity" cells={kpiCells} />

      {/* The MFA tile and the license tile, flush in a condensed grid. */}
      <CondensedGrid
        as="section"
        aria-label="MFA and licenses"
        columns={{ base: 1, 'c-lg': 2 }}
        className="mb-12"
      >
        <MfaDonut
          phish={phish ?? null}
          totalEnabled={insights?.enabledMemberUsers ?? 0}
          // Lead the tile with the most-exposed no-MFA members by name rather
          // than a bare count (Product Voice: named subjects over row counts).
          // Sourced from the dedicated No-MFA cohort so the names stay
          // consistent with the bar's count.
          noMfaPeople={props.noMfaNames}
          classificationRefining={refining}
          isLoading={props.phishLoading}
        />

        <LicenseUtilizationCard
          licenses={props.licenses}
          isLoading={props.licensesLoading}
          insights={insights}
        />
      </CondensedGrid>

      <div className="mb-12">
        <UserInsightsTrends
          trends={props.trendsData}
          isLoading={props.trendsLoading}
          days={props.trendDays}
          onDaysChange={props.onTrendDaysChange}
        />
      </div>

      {/* People needing attention — top 6 from the user-issues endpoint. */}
      <PeopleNeedingAttention
        users={props.peopleNeedingAttention}
        isLoading={props.peopleNeedingAttentionLoading}
      />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Overview · MFA tile                                                        */
/* -------------------------------------------------------------------------- */

/** The MFA tile: the share of members on MFA, a named lead, the two-part MFA
 *  bar (members without MFA in the strong neutral, with MFA in the off gray,
 *  split by a 1px gap; DESIGN.md "MFA bar") and the method split of those on
 *  MFA as proportional bars. It reads the same at 19 members or 4,000, with
 *  no mark per member (the Proportional Chart Rule).
 *
 *  §2.3 relabels the middle bucket from "Authenticator app" to "Standard MFA"
 *  because it covers push, TOTP, SMS and voice — not just authenticator-app
 *  users.
 *
 *  Keeps its historical name (it drew a donut) because the classification
 *  guard test imports it. Exported as a test seam for that gate. */
export function MfaDonut({
  phish,
  totalEnabled,
  noMfaPeople,
  classificationRefining,
  isLoading = false,
}: {
  phish: import('@/types').PhishResistantMfaSummary | null
  totalEnabled: number
  /** No-MFA members (already loaded for "People needing attention"). We lead the
   *  tile with the most-exposed of them by name. */
  noMfaPeople: import('@/types').UserDetailItem[]
  /** EXO classification hasn't run — the phish / standard / no-MFA split
   *  excludes shared/resource mailboxes and is unreliable. */
  classificationRefining: boolean
  /** The phish-resistant summary is still loading: a skeleton, never "No MFA data yet". */
  isLoading?: boolean
}) {
  const phishUsers = phish?.phishResistantMfaUsers ?? 0
  const standardUsers = phish?.standardMfaOnlyUsers ?? 0
  const noMfaUsers = phish?.noMfaUsers ?? 0

  const total = phishUsers + standardUsers + noMfaUsers
  const withMfa = phishUsers + standardUsers
  const onMfaPct =
    totalEnabled > 0
      ? Math.round(((phishUsers + standardUsers) / totalEnabled) * 100)
      : 0

  // Names of the most-exposed no-MFA members, when the already-loaded list
  // surfaced any. Falls back to the count when no names are in hand so we
  // never fabricate.
  const exposedNames = noMfaPeople
    .map(u => u.displayName?.trim())
    .filter((n): n is string => !!n)
    .slice(0, 2)

  const headingId = 'user-insights-mfa-heading'

  // Classification refining: keep the tile and its heading, replace the whole
  // body (every figure depends on the mailbox exclusion) with the advisory
  // copy. No error styling — a transient provisioning note.
  if (classificationRefining) {
    return (
      <Tile as="section" aria-labelledby={headingId} className="flex min-w-0 flex-col">
        <TileHeader title="MFA adoption" category="identity" id={headingId} />
        <p className="m-0 mt-4 text-body text-text-secondary">
          These {CLASSIFICATION_REFINING_COPY} The adoption figures return once it completes.
        </p>
      </Tile>
    )
  }

  return (
    <Tile as="section" aria-labelledby={headingId} className="flex min-w-0 flex-col">
      <TileHeader title="MFA adoption" category="identity" id={headingId} />

      {isLoading ? (
        <Skeleton lines={3} label="Loading MFA adoption" className="mt-6" />
      ) : total > 0 ? (
        <>
          <BigNumber value={onMfaPct} suffix="%" size="figure-lg" className="mt-5" />
          <p className="m-0 mt-1 text-helper text-text-helper">of enabled members are on MFA</p>

          {/* Advisor lead — names the exposed members when we have them;
              never a bare row count. */}
          <MfaLead
            total={total}
            noMfaUsers={noMfaUsers}
            onMfaPct={onMfaPct}
            names={exposedNames}
          />

          <SegmentedMeter
            className="mt-6"
            caption="Members by MFA"
            segments={[
              { id: 'without', value: noMfaUsers, tone: 'strong', label: 'without MFA' },
              { id: 'with', value: withMfa, tone: 'off', label: 'with MFA' },
            ]}
          />

          <WorkloadBars
            className="mt-6"
            caption="Members on MFA, by method"
            rows={[
              { label: 'Phish-resistant', value: phishUsers },
              { label: 'Standard MFA', value: standardUsers },
            ]}
          />
          <p className="m-0 mt-2 text-helper text-text-helper">
            Phish-resistant covers FIDO2 keys, Windows Hello and certificates. Standard MFA covers
            push, one-time codes, SMS and voice.
          </p>

          <MfaPhishFooter total={total} phishUsers={phishUsers} />
        </>
      ) : (
        <MfaLead total={total} noMfaUsers={noMfaUsers} onMfaPct={onMfaPct} names={exposedNames} />
      )}
    </Tile>
  )
}

/** Advisor lead line for the MFA tile — names exposed members when we have
 *  them, otherwise frames the count as people. */
function MfaLead({
  total,
  noMfaUsers,
  onMfaPct,
  names,
}: {
  total: number
  noMfaUsers: number
  onMfaPct: number
  names: string[]
}) {
  if (total === 0) {
    return (
      <p className="m-0 mt-4 text-body text-text-secondary">
        No MFA data yet. It appears after the next scan.
      </p>
    )
  }

  if (noMfaUsers === 0) {
    return (
      <p className="m-0 mt-3 text-body text-text-secondary">
        <b className="font-semibold text-text-primary">Every member is covered.</b>{' '}
        {onMfaPct}% on MFA.
      </p>
    )
  }

  // Lead with names when we have them; "and N others" carries the rest of the
  // bar's no-MFA total. Guard the remainder so a count mismatch between the
  // two data sources can never render a negative.
  if (names.length > 0) {
    const rest = Math.max(0, noMfaUsers - names.length)
    return (
      <p className="m-0 mt-3 text-body text-text-secondary">
        <b className="font-semibold text-text-primary">{names.join(', ')}</b>
        {rest > 0 && (
          <>
            {' '}
            and{' '}
            <b className="font-semibold text-text-primary">
              {rest.toLocaleString()} other{rest === 1 ? '' : 's'}
            </b>
          </>
        )}{' '}
        can still sign in without MFA. Close these first.
      </p>
    )
  }

  return (
    <p className="m-0 mt-3 text-body text-text-secondary">
      <b className="font-semibold text-text-primary">
        {noMfaUsers.toLocaleString()} member{noMfaUsers === 1 ? '' : 's'}
      </b>{' '}
      can still sign in without MFA. Close these first.
    </p>
  )
}

/** Phish-resistant posture footer for the MFA tile, from data already on the
 *  summary. FIDO2 / Windows Hello is the strongest tier, so we surface it
 *  whether or not anyone has adopted it yet. */
function MfaPhishFooter({ total, phishUsers }: { total: number; phishUsers: number }) {
  if (total === 0) return null
  return (
    <p className="m-0 mt-6 border-t border-line-subtle pt-4 text-helper text-text-helper">
      {phishUsers > 0 ? (
        <>
          <b className="font-semibold text-text-primary">{phishUsers.toLocaleString()}</b> of those
          covered use phish-resistant methods (FIDO2 / Windows Hello), the strongest tier.
        </>
      ) : (
        <>
          No one&rsquo;s on phish-resistant methods yet. FIDO2 / Windows Hello is the strongest tier to move toward.
        </>
      )}
    </p>
  )
}

/* -------------------------------------------------------------------------- */
/* Overview · License utilization tile                                        */
/* -------------------------------------------------------------------------- */

/** License utilization: one two-part bar per paid SKU (unassigned / assigned)
 *  — §2.3 explicitly forbids per-SKU dormant/disabled because we only have
 *  those numbers at the tenant level. The tenant-level summary rolls up above
 *  the per-SKU bars. */
function LicenseUtilizationCard({
  licenses,
  isLoading,
  insights,
}: {
  licenses: import('@/types').LicenseSkuSummary[]
  isLoading: boolean
  insights: import('@/types').UserInsightsSummary | null | undefined
}) {
  // Paid per-seat SKUs only, so the bars aren't dominated by 0%-utilized free
  // seats or storage add-ons (shared with the Licenses tab: lib/license-skus).
  const paid = licenses.filter(l => isPaidSeatSku(l.skuPartNumber))

  // Sort by utilization desc, top 6.
  const sorted = [...paid].sort(
    (a, b) => b.utilizationPercentage - a.utilizationPercentage,
  )
  const top = sorted.slice(0, 6)

  // Tenant-level aggregated counts that ARE available (per §2.3) — these
  // surface above the per-SKU bars so the disabled/dormant story is visible
  // at the right granularity.
  const disabledLicensed = insights?.disabledUsersWithLicenses ?? 0
  const inactiveLicensed = insights?.inactiveUsersWithLicenses ?? 0
  const headingId = 'user-insights-license-heading'

  return (
    <Tile as="section" aria-labelledby={headingId} className="flex min-w-0 flex-col">
      <TileHeader title="License utilization" id={headingId} />

      {(disabledLicensed > 0 || inactiveLicensed > 0) && (
        <p className="m-0 mt-3 text-body text-text-secondary">
          <b className="font-semibold text-text-primary">{disabledLicensed.toLocaleString()} disabled</b> and{' '}
          <b className="font-semibold text-text-primary">{inactiveLicensed.toLocaleString()} dormant</b>{' '}
          accounts still hold licenses across all SKUs. Reclaim those seats first.
        </p>
      )}

      {isLoading ? (
        <Skeleton lines={3} label="Loading licenses" className="mt-6" />
      ) : top.length === 0 ? (
        <p className="m-0 mt-4 text-body text-text-secondary">
          No license SKUs to display. License data appears after the next scan.
        </p>
      ) : (
        <ul className="m-0 mt-6 grid list-none gap-5 p-0">
          {top.map(sku => (
            <SkuRow key={sku.skuId} sku={sku} />
          ))}
        </ul>
      )}
    </Tile>
  )
}

/** One SKU: the name and its assignment over a two-part bar (unassigned in
 *  the strong neutral, assigned in the off gray), with a key of both counts. */
function SkuRow({ sku }: { sku: import('@/types').LicenseSkuSummary }) {
  return (
    <li className="min-w-0">
      <SegmentedMeter
        caption={
          <span className="flex min-w-0 items-baseline justify-between gap-4">
            <b className="min-w-0 truncate text-body font-semibold text-text-primary">
              {sku.displayName || sku.skuPartNumber}
            </b>
            <span className="flex-none tabular-nums">
              {sku.usedLicenses.toLocaleString()} of {sku.totalLicenses.toLocaleString()} assigned ·{' '}
              {sku.utilizationPercentage}%
            </span>
          </span>
        }
        segments={[
          { id: 'available', value: sku.availableLicenses, tone: 'strong', label: 'unassigned' },
          { id: 'used', value: sku.usedLicenses, tone: 'off', label: 'assigned' },
        ]}
      />
    </li>
  )
}

/* -------------------------------------------------------------------------- */
/* Overview · People needing attention                                        */
/* -------------------------------------------------------------------------- */

/** Severity by issueType. Per §2.3 we DO NOT tier by directory role (we don't
 *  ingest those today); we use the user's issueType as the proxy, which is
 *  honest with the data we have. */
function severityForIssue(
  issueType: string | undefined,
): 'crit' | 'high' | 'med' | 'low' {
  if (!issueType) return 'low'
  switch (issueType) {
    case 'NoMfa':
      return 'crit'
    case 'NoPhishResistantMfa':
    case 'DisabledWithLicenses':
      return 'high'
    case 'InactiveMembers':
    case 'InactiveWithLicenses':
    case 'LicenseWaste':
      return 'med'
    default:
      return 'low'
  }
}

function reasonLineForIssue(user: UserDetailItem): string {
  switch (user.issueType) {
    case 'NoMfa':
      return 'No MFA enrolled'
    case 'NoPhishResistantMfa':
      return 'Standard MFA · upgrade to phish-resistant'
    case 'DisabledWithLicenses':
      return 'Disabled · still licensed'
    case 'InactiveMembers':
    case 'InactiveWithLicenses':
      return 'Inactive · seat held'
    case 'LicenseWaste':
      return 'License waste'
    default:
      return 'Needs attention'
  }
}

/** Relative-time string from an ISO date ("Last sign-in 3d ago"). Returns
 *  "Never signed in" for users who have no sign-in record. */
function relativeSignIn(iso?: string): string {
  if (!iso) return 'Never signed in'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'Never signed in'
  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffDays < 1) return 'Last sign-in today'
  if (diffDays === 1) return 'Last sign-in yesterday'
  if (diffDays < 30) return `Last sign-in ${diffDays}d ago`
  if (diffDays < 365) return `Last sign-in ${Math.floor(diffDays / 30)}mo ago`
  return `Last sign-in ${Math.floor(diffDays / 365)}y ago`
}

function PeopleNeedingAttention({
  users,
  isLoading,
}: {
  users: UserDetailItem[]
  isLoading: boolean
}) {
  const columns: DataColumn<UserDetailItem>[] = [
    {
      id: 'severity',
      header: 'Severity',
      nowrap: true,
      width: '128px',
      cell: user => <SeverityGlyph level={severityForIssue(user.issueType)} />,
    },
    {
      id: 'person',
      header: 'Person',
      primary: true,
      cell: user => (
        <TableItem
          title={user.displayName || user.userPrincipalName}
          // §2.3: NOT directory roles — jobTitle, real, or fall back to "Member".
          sub={user.jobTitle?.trim() || (user.userType === 'Guest' ? 'Guest' : 'Member')}
        />
      ),
    },
    {
      id: 'issue',
      header: 'Issue',
      cell: user => (
        <>
          <span className="block">{reasonLineForIssue(user)}</span>
          <small className="mt-0.5 block text-helper text-text-helper">
            {relativeSignIn(user.lastSignInDateTime)}
          </small>
        </>
      ),
    },
    {
      id: 'open',
      header: <span className="sr-only">Open in Microsoft 365</span>,
      align: 'end',
      nowrap: true,
      cell: user => {
        // Per §5: the action is "Open user" — NOT "Send setup link" (a
        // backlog item in posture-pages-supplements.md, Tandm task
        // task-5AIL55HW). Deep-links into the M365 admin center.
        const adminLink = `https://admin.microsoft.com/Adminportal/Home#/users/:/UserDetails/${encodeURIComponent(user.userId)}`
        const name = user.displayName || user.userPrincipalName
        return (
          <a
            href={adminLink}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${name} in the Microsoft 365 admin center`}
            className="inline-flex items-center gap-2 text-link transition-colors duration-110 ease-carbon hover:text-link-hover hover:underline"
          >
            Open user
            <IconExternal />
          </a>
        )
      },
    },
  ]

  return (
    <DataTable
      title="People needing attention"
      description="Members with an open MFA, license or sign-in issue, the most pressing first."
      columns={columns}
      rows={users}
      rowId={user => user.userId}
      rowLabel={user => user.displayName || user.userPrincipalName}
      isLoading={isLoading}
      loadingRows={4}
      empty="No members have open MFA, license, or inactivity issues. Nothing to review."
      footer={
        <Link
          to="/user-insights?tab=all"
          className="inline-flex items-center gap-2 text-link transition-colors duration-110 ease-carbon hover:text-link-hover hover:underline"
        >
          Open the user directory
          <IconArrowRight />
        </Link>
      }
    />
  )
}
